import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  Info,
  CheckCircle2,
  Building2,
  UserRound,
  Lock,
  ArrowLeft,
  School,
} from "lucide-react";
import { WhatsAppIcon } from "../components/icons/BrandIcons";
import axiosClient from "../api/axiosClient";
import {
  setAuthToken,
  setRefreshToken,
  decodeToken,
  getAppUrlForUser,
} from "@conext/auth";
import { decodeLeadToken } from "../utils/leadToken";
import { readSignupUtm } from "../utils/signupUtm";
import { trackOpenAiLeadCreated } from "../utils/openaiPixel";
import {
  SECTOR_DETAIL_MAX,
  SECTOR_DETAIL_TEXT,
  sectorDetailPayload,
} from "../utils/sectorDetail";
import {
  AUTOESCUELA_TRABAJO_MAX,
  PROFESOR_AUTOESCUELA_TEXT,
  profesorAutoescuelaPayload,
} from "../utils/profesorAutoescuela";
import { ALTA_TEXT, ERRORES_CA, ERROR_GENERICO, altaLang } from "../utils/altaIdioma";
import PhonePrefixSelector from "../components/common/PhonePrefixSelector";
import logo from "../assets/LOGO_AZUL_SIN_FONDO.png";

// Prefijos con los que se puede dar de alta: LISTA CERRADA, la misma que
// ALLOWED_SIGNUP_PREFIXES de aws_lambdas/a_publicSignupStart/signupPhone.js (cada código es
// una plantilla de pago). Ampliar = añadir el prefijo en los dos sitios.
const ALLOWED_SIGNUP_PREFIXES = ["+34"];

// Prefijo + número local → E.164. Si ya lo teclean con «+», se respeta tal cual; «0034…» →
// «+34…»; y si repiten el prefijo del selector («34612345678» con +34) no se duplica.
const buildPhone = (prefix, raw) => {
  const txt = String(raw || "").trim();
  const digits = txt.replace(/[^0-9]/g, "");
  if (txt.startsWith("+")) return digits ? "+" + digits : "";
  if (digits.startsWith("00")) return "+" + digits.slice(2);
  const p = prefix.replace(/[^0-9]/g, "");
  if (p && digits.startsWith(p) && digits.length >= p.length + 9) return prefix + digits.slice(p.length);
  const local = digits.replace(/^0+/, "");
  return local ? prefix + local : "";
};

const prefixAllowed = (e164) => ALLOWED_SIGNUP_PREFIXES.some((p) => e164.startsWith(p));

// «+34612345678» → «+34 612 345 678» para la pantalla del código (el dueño comprueba que lo
// tecleó bien). Otros países se enseñan tal cual.
const formatPhone = (e164) => {
  const m = String(e164 || "").match(/^\+34(\d{3})(\d{3})(\d{3})$/);
  return m ? `+34 ${m[1]} ${m[2]} ${m[3]}` : String(e164 || "");
};

// Sectores disponibles. Lista estática: el registro aún no tiene token, así
// que no podemos llamar a /admin/sector-config. Los `id` deben coincidir
// EXACTAMENTE con las claves de c_sector_config / v_create_client (deciden
// app_type, features y aprovisionamiento). Ojo: "peluquerias" (plural).
const SECTORS = [
  { id: "peluquerias", label: "Peluquería" },
  { id: "barberia", label: "Barbería" },
  { id: "estetica", label: "Estética" },
  { id: "fisioterapia", label: "Fisioterapia" },
  { id: "lavadero", label: "Lavadero" },
  { id: "salud", label: "Salud / Clínica" },
  { id: "spa", label: "Spa" },
  { id: "consultoria", label: "Consultoría" },
  { id: "despacho_abogados", label: "Despacho de abogados" },
  { id: "comida_para_llevar", label: "Comida para llevar" },
  { id: "autoescuela", label: "Autoescuela" },
  { id: "marketing", label: "Marketing" },
  { id: "otro", label: "Otro" },
];

// Mapea los códigos de error del backend a mensajes en español.
const ERROR_MESSAGES = {
  business_name_required: "Introduce el nombre de tu negocio.",
  invalid_usuario: "Introduce tu móvil con WhatsApp (con su prefijo si no es español).",
  weak_password: "La contraseña debe tener al menos 8 caracteres.",
  sector_required: "Selecciona el tipo de negocio.",
  usuario_en_uso: "Ese usuario ya tiene una cuenta. Inicia sesión.",
  email_in_use: "Ese usuario ya tiene una cuenta. Inicia sesión.",
  too_many_requests: "Demasiados intentos. Inténtalo más tarde.",
  too_many_pending_requests: "Demasiados intentos. Inténtalo más tarde.",
  too_many_codes: "Ya te hemos enviado 3 códigos en la última hora. Espera un poco y vuelve a pedirlo.",
  whatsapp_send_failed: "No pudimos enviarte el WhatsApp. Revisa el número e inténtalo de nuevo.",
  invalid_code: "Código incorrecto.",
  code_expired: "El código ha caducado. Pide uno nuevo.",
  too_many_attempts: "Demasiados intentos. Empieza el registro de nuevo.",
  signup_not_found: "Registro no encontrado. Empieza de nuevo.",
  signup_not_pending: "Este registro ya no es válido. Empieza de nuevo.",
  invalid_lead_token: "El enlace ha caducado. Crea tu cuenta con el formulario.",
  sector_detail_required: SECTOR_DETAIL_TEXT.es.required,
};

// Textos del campo «¿A qué se dedica?». La página entera está en español (y el alta fija
// language "es" en el backend): va/en quedan listos en utils/sectorDetail.js.
const SD_TEXT = SECTOR_DETAIL_TEXT.es;
// Plan Profesor: «¿Con qué autoescuela trabajas?» en el idioma del alta (PA_TEXT, dentro del componente).

// lang "ca": solo el alta del Plan Profesor con ?lang=ca (utils/altaIdioma.js); el resto, siempre "es".
const errorTextLang = (code, fallback, lang = "es") =>
  (lang === "ca" ? ERRORES_CA[code] : ERROR_MESSAGES[code]) || fallback || ERROR_GENERICO[lang] || ERROR_GENERICO.es;

// ?sector=lavadero preselecciona el sector (lo envía el número oficial de
// WhatsApp cuando ya conoce el sector del lead). Un valor desconocido se ignora.
const sectorFromQuery = () => {
  const param = new URLSearchParams(window.location.search).get("sector");
  return SECTORS.some((s) => s.id === param) ? param : "";
};

// ?plan=profesor (20/10/2026): alta del plan «Profesor» de autoescuelas (un profesor que lleva solo su agenda,
// 15 €/mes). Sector fijo en autoescuela, textos de profesor y botón de WhatsApp a soporte en toda la pantalla.
// Ficha: docs/features/autoescuela-plan-profesor.md
const planProfesorFromQuery = () => new URLSearchParams(window.location.search).get("plan") === "profesor";
const SOPORTE_WHATSAPP = "34686851695";
const waProfesor = (texto) => `https://wa.me/${SOPORTE_WHATSAPP}?text=${encodeURIComponent(texto)}`;

// Token `lt` del alta en 1 paso (Task 1): decodificado sin verificar, solo para
// pintar. La firma la valida el backend en /public/signup/start.
const leadFromQuery = () =>
  decodeLeadToken(new URLSearchParams(window.location.search).get("lt"));

const Signup = () => {
  const rawLeadTokenRef = useRef(
    new URLSearchParams(window.location.search).get("lt") || ""
  );
  // Codigo de recomendacion del enlace que le paso el negocio que le recomendo. No es un
  // secreto y no bloquea nada: si es invalido, el alta sigue igual y la atribucion se
  // resuelve por telefono.
  const refCodeRef = useRef(
    (new URLSearchParams(window.location.search).get("ref") || "").trim().toUpperCase(),
  );
  // UTM del anuncio: se leen una vez al entrar y viajan en TODAS las llamadas a /start.
  const utmRef = useRef(readSignupUtm(window.location.search));
  const [lead, setLead] = useState(leadFromQuery); // null = flujo generico
  const [leadExpired] = useState(() => {
    const raw = new URLSearchParams(window.location.search).get("lt");
    return Boolean(raw) && !decodeLeadToken(raw); // habia token pero no vale
  });
  const [step, setStep] = useState(() => (leadFromQuery() ? "lead" : "form")); // lead | form | code
  const [businessName, setBusinessName] = useState("");
  // Alta SOLO con móvil (27/9/2026): sin teléfono no hay seguimiento (setter, rescate, fin de
  // prueba). `usuario` guarda el E.164 que se envió: lo reusan Reenviar y «ya tienes cuenta».
  const [usuario, setUsuario] = useState("");
  const [phonePrefix, setPhonePrefix] = useState("+34");
  const [phoneLocal, setPhoneLocal] = useState("");
  const [password, setPassword] = useState("");
  const [planProfesor] = useState(planProfesorFromQuery);
  // Plan Profesor en catalán (21/10/2026): ?plan=profesor&lang=ca. Fuera del plan profesor, siempre "es".
  const [lang] = useState(() => altaLang(window.location.search));
  const L = ALTA_TEXT[lang];
  const PA_TEXT = PROFESOR_AUTOESCUELA_TEXT[lang] || PROFESOR_AUTOESCUELA_TEXT.es;
  const errorText = (code, fallback) => errorTextLang(code, fallback, lang);
  React.useEffect(() => {
    if (lang !== "ca") return undefined;
    const antes = document.documentElement.lang;
    document.documentElement.lang = "ca";
    return () => { document.documentElement.lang = antes; };
  }, [lang]);
  const [sector, setSector] = useState(() => (planProfesorFromQuery() ? "autoescuela" : leadFromQuery()?.sector || sectorFromQuery()));
  // Va en las llamadas a /start: solo con ?plan=profesor (el backend lo ignora fuera de autoescuela).
  const planBody = planProfesor ? "profesor" : undefined;
  // Solo "ca" (y solo con plan profesor) viaja al backend; en castellano el cuerpo no cambia.
  const langBody = planProfesor && lang === "ca" ? "ca" : undefined;
  const [sectorDetail, setSectorDetail] = useState("");
  // Va en TODAS las llamadas a /start (undefined si el sector no es «otro»).
  const sectorDetailBody = sectorDetailPayload(sector, sectorDetail);
  // Plan Profesor (21/10/2026): con qué autoescuela trabaja, opcional. Va en TODAS las llamadas a /start.
  const [autoescuelaTrabajo, setAutoescuelaTrabajo] = useState("");
  const [porSuCuenta, setPorSuCuenta] = useState(false);
  const profesorAutoescuelaBody = profesorAutoescuelaPayload(planProfesor, autoescuelaTrabajo, porSuCuenta);
  const [code, setCode] = useState("");

  const [signupId, setSignupId] = useState("");
  const [destination, setDestination] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Usuario tecleado cuando el backend responde "ya tiene cuenta" (usuario_en_uso /
  // email_in_use): empuja a login/recuperación en vez de solo pintar el error rojo.
  const [existingUser, setExistingUser] = useState(null);

  // Fija existingUser de forma segura: nunca "" (pantalla muda, sin error rojo ni
  // bloque azul) y sin espacios internos (leads antiguos guardaron el teléfono con
  // espacios, "+34 632106181", y ?u= debe llevar un username que el login acepte).
  const applyExistingUser = (raw) => {
    const cleaned = String(raw || "").replace(/\s+/g, "").trim();
    if (cleaned) {
      setExistingUser(cleaned);
      setError("");
    } else {
      setExistingUser(null);
      setError(errorText("usuario_en_uso"));
    }
  };

  const handleStart = async (e) => {
    e.preventDefault();
    setError("");
    setExistingUser(null);

    if (!businessName.trim()) return setError(planProfesor ? L.nombreRequerido : errorText("business_name_required"));
    const fullPhone = buildPhone(phonePrefix, phoneLocal);
    // Mínimo de E.164 (7 dígitos); el formato exacto lo valida a_publicSignupStart.
    if (fullPhone.replace(/[^0-9]/g, "").length < 7 || !prefixAllowed(fullPhone)) {
      return setError(errorText("invalid_usuario"));
    }
    if (password.length < 8) return setError(errorText("weak_password"));
    if (!sector) return setError(errorText("sector_required"));
    if (sector === "otro" && !sectorDetailBody) return setError(errorText("sector_detail_required"));

    setLoading(true);
    try {
      const res = await axiosClient.post("/public/signup/start", {
        businessName: businessName.trim(),
        usuario: fullPhone,
        password,
        sector,
        sector_detail: sectorDetailBody,
        plan: planBody,
        lang: langBody,
        ...profesorAutoescuelaBody,
        refCode: refCodeRef.current || undefined,
        utm: utmRef.current,
      });
      setUsuario(fullPhone);
      setSignupId(res.signupId);
      setDestination(res.destination || "");
      setCode("");
      setStep("code");
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === "usuario_en_uso" || code === "email_in_use") {
        applyExistingUser(fullPhone);
      } else {
        setError(errorText(code, err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLeadStart = async (e) => {
    e.preventDefault();
    setError("");
    setExistingUser(null);

    if (password.length < 8) return setError(errorText("weak_password"));
    if (!sector) return setError(errorText("sector_required"));
    if (sector === "otro" && !sectorDetailBody) return setError(errorText("sector_detail_required"));

    setLoading(true);
    try {
      const res = await axiosClient.post("/public/signup/start", {
        leadToken: rawLeadTokenRef.current,
        password,
        sector,
        sector_detail: sectorDetailBody,
        plan: planBody,
        lang: langBody,
        ...profesorAutoescuelaBody,
        refCode: refCodeRef.current || undefined,
        utm: utmRef.current,
      });
      setSignupId(res.signupId);
      setDestination(res.destination || "");
      setCode("");
      setStep("code");
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === "invalid_lead_token") {
        setLead(null);
        setStep("form");
      }
      if (code === "usuario_en_uso" || code === "email_in_use") {
        applyExistingUser(lead?.telefono || "");
      } else {
        setError(errorText(code, err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setExistingUser(null);

    if (!/^\d{6}$/.test(code.trim())) return setError(errorText("invalid_code"));

    setLoading(true);
    try {
      // Cookies del Pixel de Meta: viven en .conextcrm.es (las dejó la landing),
      // compartidas con este subdominio. El backend dispara la CAPI
      // CompleteRegistration con ellas — aquí no hay Pixel instalado.
      const getCookie = (n) =>
        document.cookie.split("; ").find((c) => c.startsWith(n + "="))?.split("=")[1];
      const eventId =
        window.crypto?.randomUUID?.() || `sv-${signupId}-${Date.now()}`;
      const res = await axiosClient.post("/public/signup/verify", {
        signupId,
        code: code.trim(),
        meta_event_id: eventId,
        fbp: getCookie("_fbp"),
        fbc: getCookie("_fbc"),
        event_source_url: window.location.href,
      });
      if (!res || !res.token) throw new Error("Token no recibido");

      setAuthToken(res.token);
      if (res.refresh_token) setRefreshToken(res.refresh_token);

      // Conversión de Google Ads al crear la cuenta real.
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-17990566700/l3LmCLzgroIcEKyGyYJD",
          value: 1.0,
          currency: "EUR",
        });
      }

      // ChatGPT Ads: alta completada = `lead_created` (el evento al que optimizan sus
      // campañas). Sin SDK (bloqueador) no hace nada. Mismo id que la CAPI de Meta.
      const oaiSent = trackOpenAiLeadCreated({
        vertical: sector,
        plan: planBody || "estandar",
        eventId,
      });

      // Redirección universal por app_type: citas, pedidos, autoescuela o
      // marketing — mismo camino que el login central (/auth-callback guarda
      // el token y entra al dashboard). La cuenta nace aprovisionada y con el
      // onboarding completado.
      const payload = decodeToken(res.token);
      const appUrl = getAppUrlForUser(payload);
      const goToApp = () => {
        window.location.href =
          appUrl + "/auth-callback?token=" + encodeURIComponent(res.token);
      };
      // Un respiro para que el SDK de OpenAI saque el evento (además vacía la cola en
      // pagehide con sendBeacon); sin píxel, la redirección es inmediata como antes.
      if (oaiSent) setTimeout(goToApp, 300);
      else goToApp();
      // Sin setLoading(false): abandonamos la página vía redirect.
    } catch (err) {
      const code = err.response?.data?.error;
      // Carrera: la cuenta se crea entre /start y /verify (ver a_publicSignupVerify).
      // Mismo tratamiento que handleStart/handleLeadStart: empuja a login/recuperación
      // en vez de solo pintar el error rojo.
      if (code === "usuario_en_uso" || code === "email_in_use") {
        applyExistingUser(lead ? lead?.telefono || "" : usuario.trim());
      } else {
        setError(errorText(code, err.message));
      }
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setExistingUser(null);
    setLoading(true);
    try {
      const res = await axiosClient.post(
        "/public/signup/start",
        lead
          ? {
              leadToken: rawLeadTokenRef.current,
              password,
              sector,
              sector_detail: sectorDetailBody,
              plan: planBody,
              lang: langBody,
              ...profesorAutoescuelaBody,
              refCode: refCodeRef.current || undefined,
              utm: utmRef.current,
            }
          : {
              businessName: businessName.trim(),
              usuario: usuario.trim(),
              password,
              sector,
              sector_detail: sectorDetailBody,
              plan: planBody,
              lang: langBody,
              ...profesorAutoescuelaBody,
              refCode: refCodeRef.current || undefined,
              utm: utmRef.current,
            }
      );
      setSignupId(res.signupId);
      setDestination(res.destination || destination);
      setCode("");
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === "usuario_en_uso" || code === "email_in_use") {
        applyExistingUser(lead ? lead?.telefono || "" : usuario.trim());
      } else {
        setError(errorText(code, err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "block w-full pl-12 pr-4 py-4 bg-white border border-gray-100 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-medium text-gray-700 placeholder:text-gray-300 disabled:opacity-50";

  // Grid de sectores, compartido por el paso "form" (generico) y "lead".
  const renderSectorGrid = () => (planProfesor ? null : (
    <div>
      <label className="block font-bold uppercase tracking-widest text-[11px] text-gray-400 mb-2.5 ml-1">
        Tipo de negocio
      </label>
      <div className="grid grid-cols-2 gap-2">
        {SECTORS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setSector(s.id)}
            disabled={loading}
            className={`px-3 py-2.5 rounded-xl text-sm font-semibold border transition-all text-left ${
              sector === s.id
                ? "bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-200/50"
                : "bg-white text-gray-700 border-gray-100 hover:border-blue-300"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {sector === "otro" && (
        <div className="mt-3">
          <label
            htmlFor="signup-sector-detail"
            className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1"
          >
            {SD_TEXT.label}
          </label>
          <input
            id="signup-sector-detail"
            type="text"
            value={sectorDetail}
            onChange={(e) => setSectorDetail(e.target.value)}
            placeholder={SD_TEXT.placeholder}
            maxLength={SECTOR_DETAIL_MAX}
            aria-required="true"
            disabled={loading}
            className="block w-full px-4 py-3 bg-white border border-gray-100 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-medium text-gray-700 placeholder:text-gray-300 disabled:opacity-50"
          />
        </div>
      )}
    </div>
  ));

  return (
    <div className="h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 flex justify-center p-4 sm:p-6 relative overflow-y-auto">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-100/40 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-100/40 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        ></div>
      </div>

      <div className="relative bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 p-6 sm:p-10 w-full max-w-md my-auto">
        <div className="text-center mb-8">
          <img
            src={logo}
            alt="ConextCRM"
            className="w-52 h-auto mx-auto mb-5 drop-shadow-sm"
          />
          <p className="font-bold text-lg text-gray-800">
            {planProfesor ? L.titleProfesor : "Crea tu cuenta gratis"}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {/* Autoescuelas: 15 días de prueba desde el 16/10/2026 (aws_lambdas/_shared/trialDays.js); el resto, 1 mes. */}
            {planProfesor
              ? L.subProfesor
              : `Prueba ${sector === "autoescuela" ? "15 días" : "1 mes"} · sin tarjeta · configúralo en minutos`}
          </p>
          {planProfesor && (
            <a href={waProfesor(L.waProfesor)} target="_blank" rel="noopener noreferrer" data-testid="signup-wa-soporte"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1.5 text-sm font-semibold text-green-800 hover:bg-green-100">
              <WhatsAppIcon size={15} className="text-[#25D366]" /> {L.dudas}
            </a>
          )}
          {planProfesor && (
            <p className="mt-3 text-xs text-gray-500" data-testid="signup-profesor-dueno">
              {L.duenoPregunta}{" "}
              <a href="https://conextcrm.es/autoescuelas#agenda" className="font-semibold text-blue-600 hover:text-blue-700">{L.duenoDemo}</a>
            </p>
          )}
        </div>

        {existingUser && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-2xl">
            <p className="text-sm font-semibold text-blue-900 mb-3">
              {L.yaTieneCuenta}
            </p>
            <div className="flex flex-col gap-2">
              <Link
                to={`/login?u=${encodeURIComponent(existingUser)}`}
                className="w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-4 rounded-xl transition-colors"
              >
                {L.iniciarSesion}
              </Link>
              <Link
                to={`/forgot-password?u=${encodeURIComponent(existingUser)}`}
                className="w-full text-center text-sm font-semibold text-blue-700 hover:text-blue-800 py-1"
              >
                {L.olvide}
              </Link>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 text-red-600 rounded-lg flex-shrink-0 mt-0.5">
                <AlertCircle size={18} />
              </div>
              <p className="text-sm font-semibold text-red-800">{error}</p>
            </div>
          </div>
        )}

        {leadExpired && step === "form" && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-2xl">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 text-blue-600 rounded-lg flex-shrink-0 mt-0.5">
                <Info size={18} />
              </div>
              <p className="text-sm font-semibold text-blue-800">
                {L.enlaceCaducado}
              </p>
            </div>
          </div>
        )}

        {step === "lead" && lead && (
          <form onSubmit={handleLeadStart} className="space-y-5">
            <div className="text-center">
              <p className="font-bold text-lg text-gray-800">
                Hola {lead.nombre || "!"}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {L.tuCuentaPara} <span className="font-bold text-gray-700">{lead.negocio}</span>
              </p>
            </div>

            {/* Telefono fijo, no editable */}
            <div className="flex items-center gap-3 px-4 py-4 bg-green-50 border border-green-100 rounded-2xl">
              <div className="p-2 bg-green-100 text-green-600 rounded-lg flex-shrink-0">
                <WhatsAppIcon size={18} className="text-[#25D366]" />
              </div>
              <div>
                <p className="font-bold text-gray-800">{lead.telefono}</p>
                <p className="text-xs text-gray-500">
                  {L.codigoAEste}
                </p>
              </div>
            </div>

            {renderSectorGrid()}

            {/* Contraseña */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-blue-600" />
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={L.password}
                disabled={loading}
                className={inputBase}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-200/50 hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] transition-all disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5 mr-2" />
                  <span>{L.enviando}</span>
                </>
              ) : (
                <span>{L.enviar}</span>
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setLead(null);
                  setStep("form");
                  setError("");
                  setExistingUser(null);
                }}
                disabled={loading}
                className="text-sm text-gray-500 font-medium hover:text-gray-700 disabled:opacity-50"
              >
                {L.otrosDatos}
              </button>
            </div>
          </form>
        )}

        {step === "form" && (
          <form onSubmit={handleStart} className="space-y-5">
            {/* Nombre del negocio */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                {planProfesor ? <UserRound className="h-5 w-5 text-blue-600" /> : <Building2 className="h-5 w-5 text-blue-600" />}
              </div>
              <input
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder={planProfesor ? L.nombreProfesor : "Nombre de tu negocio"}
                disabled={loading}
                className={inputBase}
              />
            </div>

            {planProfesor && (
              <p className="-mt-3 ml-1 text-xs text-gray-500">{L.asiTeVeran}</p>
            )}

            {/* Plan Profesor: con qué autoescuela trabaja (opcional; David le escribe a cada uno). */}
            {planProfesor && (
              <div data-testid="signup-profesor-autoescuela">
                <label
                  htmlFor="signup-profesor-autoescuela"
                  className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1"
                >
                  {PA_TEXT.label} <span className="font-normal text-gray-400">{PA_TEXT.optional}</span>
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <School className={`h-5 w-5 ${porSuCuenta ? "text-gray-300" : "text-blue-600"}`} />
                  </div>
                  <input
                    id="signup-profesor-autoescuela"
                    type="text"
                    value={porSuCuenta ? "" : autoescuelaTrabajo}
                    onChange={(e) => setAutoescuelaTrabajo(e.target.value)}
                    placeholder={porSuCuenta ? PA_TEXT.porSuCuenta : PA_TEXT.placeholder}
                    maxLength={AUTOESCUELA_TRABAJO_MAX}
                    autoComplete="organization"
                    disabled={loading || porSuCuenta}
                    className={inputBase}
                  />
                </div>
                <label className="mt-2 ml-1 inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={porSuCuenta}
                    onChange={(e) => setPorSuCuenta(e.target.checked)}
                    disabled={loading}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  {PA_TEXT.porSuCuenta}
                </label>
              </div>
            )}

            {/* Móvil (WhatsApp): obligatorio, con selector de prefijo. Nada de email. */}
            <div>
              <div className="flex w-full min-w-0">
                <PhonePrefixSelector
                  value={phonePrefix}
                  onChange={setPhonePrefix}
                  allowed={ALLOWED_SIGNUP_PREFIXES}
                  className="shrink-0"
                />
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  value={phoneLocal}
                  onChange={(e) => setPhoneLocal(e.target.value)}
                  placeholder={L.movil}
                  aria-required="true"
                  disabled={loading}
                  className="min-w-0 flex-1 px-4 py-4 bg-white border border-l-0 border-gray-200 rounded-r-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-medium text-gray-700 placeholder:text-gray-300 disabled:opacity-50"
                />
              </div>
              <p className="mt-2 ml-1 text-sm text-gray-600 flex items-center gap-1.5">
                <WhatsAppIcon size={13} className="text-[#25D366]" />
                {L.codigoPorWa}
              </p>
            </div>

            {/* Contraseña */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-blue-600" />
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={L.password}
                disabled={loading}
                className={inputBase}
              />
            </div>

            {/* Sector */}
            {renderSectorGrid()}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-200/50 hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] transition-all disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5 mr-2" />
                  <span>{L.enviando}</span>
                </>
              ) : (
                <span>{L.enviar}</span>
              )}
            </button>
            <p className="text-xs text-gray-400 text-center">
              {L.pediremos}
            </p>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={handleVerify} className="space-y-5">
            <div className="text-center">
              <div className="inline-flex p-3 bg-green-50 text-green-600 rounded-2xl mb-3">
                <WhatsAppIcon size={24} className="text-[#25D366]" />
              </div>
              <p className="text-sm text-gray-600">
                {L.enviadoA}
                <br />
                <span className="font-bold text-gray-800">
                  {formatPhone(lead ? lead.telefono : usuario) || destination || L.tuWhatsapp}
                </span>
              </p>
            </div>

            <div>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/[^0-9]/g, ""))
                }
                placeholder="●●●●●●"
                disabled={loading}
                className="block w-full min-w-0 text-center tracking-[0.3em] sm:tracking-[0.5em] text-2xl font-bold py-4 bg-white border border-gray-100 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all text-gray-800 placeholder:text-gray-200 disabled:opacity-50"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-200/50 hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] transition-all disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5 mr-2" />
                  <span>{L.verificando}</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-5 w-5 mr-2" />
                  <span>{L.verificar}</span>
                </>
              )}
            </button>

            <div className="flex items-center justify-between gap-2 text-sm">
              <button
                type="button"
                onClick={() => {
                  setStep(lead ? "lead" : "form");
                  setError("");
                  setExistingUser(null);
                }}
                disabled={loading}
                className="text-gray-500 font-medium hover:text-gray-700 flex items-center gap-1 disabled:opacity-50"
              >
                <ArrowLeft size={15} /> {L.cambiarDatos}
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={loading}
                className="text-right text-gray-500 font-medium disabled:opacity-50"
              >
                {L.noLlega}{" "}
                <span className="text-blue-600 font-bold hover:text-blue-700">
                  {L.reenviar}
                </span>
              </button>
            </div>

          </form>
        )}

        <div className="mt-8 pt-6 border-t border-gray-50 flex flex-col items-center gap-3">
          <div className="text-sm text-gray-600 font-medium">
            <span>{L.yaTienes}</span>
            <Link
              to="/login"
              className="text-blue-600 font-bold hover:text-blue-700 transition-colors"
            >
              {L.iniciaAqui}
            </Link>
          </div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
            © 2026 ConextCRM
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
